import type { NormalisedQuery } from "./keys.ts";

/**
 * `contracts/client-api.md` § Key scheme: `normalisedQuery` is canonical. Filters are sorted
 * by parameter name, repeated values are sorted, defaults are removed, and the result is a
 * stable object, so two URLs expressing one query share one cache entry.
 */

/** The defaults 001's list query applies when a parameter is absent (§ List query). */
export const LIST_DEFAULTS: Readonly<Record<string, string>> = {
  group_by: "none",
  order_by: "position",
  limit: "50",
  group_limit: "50",
  include_total: "false",
};

/** Parameters that address a page rather than describe a query; they never enter a key. */
const PAGE_PARAMETERS: ReadonlySet<string> = new Set(["cursor", "group_cursor"]);

/** Parameters whose repeated values are a sequence rather than a set: `order_by` applies in order (002 § Ordering). */
const ORDERED_PARAMETERS: ReadonlySet<string> = new Set(["order_by"]);

export type RawQuery = Readonly<Record<string, string | readonly string[] | number | boolean | null | undefined>> | URLSearchParams;

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value);
}

export function normaliseQuery(raw: RawQuery): NormalisedQuery {
  const entries = new Map<string, string[]>();
  const add = (name: string, value: string) => {
    const list = entries.get(name) ?? [];
    list.push(value);
    entries.set(name, list);
  };
  if (raw instanceof URLSearchParams) {
    for (const [name, value] of raw) add(name, value);
  } else {
    for (const [name, value] of Object.entries(raw)) {
      if (value === undefined || value === null) continue;
      if (isStringArray(value)) for (const v of value) add(name, v);
      else add(name, String(value));
    }
  }
  const out: Record<string, string | readonly string[]> = {};
  for (const name of [...entries.keys()].sort()) {
    if (PAGE_PARAMETERS.has(name)) continue;
    const distinct = [...new Set(entries.get(name) ?? [])];
    const values = ORDERED_PARAMETERS.has(name) ? distinct : distinct.sort();
    if (values.length === 0) continue;
    if (values.length === 1 && LIST_DEFAULTS[name] === values[0]) continue;
    const [single] = values;
    out[name] = values.length === 1 && single !== undefined ? single : values;
  }
  return out;
}

/** The query string of a normalised query, in canonical order. */
export function serialiseQuery(query: NormalisedQuery): string {
  const params = new URLSearchParams();
  for (const name of Object.keys(query).sort()) {
    const value = query[name];
    if (value === undefined) continue;
    if (isStringArray(value)) for (const v of value) params.append(name, v);
    else params.set(name, value);
  }
  return params.toString();
}

/** The fields a normalised query filters or groups on, which the event reducer compares
 * against `data.changes` to decide which lists an update invalidates. */
export function fieldsOf(query: NormalisedQuery): ReadonlySet<string> {
  const fields = new Set<string>();
  for (const name of Object.keys(query)) {
    const field = QUERY_PARAMETER_FIELD[name];
    if (field !== undefined) fields.add(field);
    // The calendar's window and its No date tray (001 § List query, 2026-09-28) read both dates.
    if (WINDOW_PARAMETERS.has(name)) {
      fields.add("start_date");
      fields.add("due_date");
    }
    // A `field[<id>]` clause of 002 reads the item's `fields`, as a grouping or an ordering by one does.
    if (name.startsWith("field[")) fields.add("fields");
    if (name === "expand") {
      const value = query[name];
      if (value !== undefined) for (const expansion of isStringArray(value) ? value : [value]) fields.add(expansion);
    }
    if (name === "group_by" || name === "order_by") {
      const value = query[name];
      for (const entry of isStringArray(value) ? value : [value ?? ""]) {
        const bare = entry.replace(/:desc$/, "");
        if (bare.startsWith("field:")) fields.add("fields");
        const mapped = GROUP_FIELD[bare];
        if (mapped !== undefined) fields.add(mapped);
      }
    }
  }
  return fields;
}

const QUERY_PARAMETER_FIELD: Readonly<Record<string, string>> = {
  state: "state_id",
  state_group: "state_id",
  assignee: "assignee_ids",
  creator: "created_by",
  label: "label_ids",
  priority: "priority",
  cycle: "cycle_id",
  module: "module_ids",
  account: "account_id",
  milestone_stage: "milestone_stage_id",
  parent: "parent_id",
  has_children: "parent_id",
  blocked: "is_blocked",
  start_after: "start_date",
  start_before: "start_date",
  due_after: "due_date",
  due_before: "due_date",
  due: "due_date",
  updated_after: "updated_at",
  updated_before: "updated_at",
  q: "title",
};

const WINDOW_PARAMETERS: ReadonlySet<string> = new Set(["due_from", "due_to", "dated"]);

const GROUP_FIELD: Readonly<Record<string, string>> = {
  state: "state_id",
  state_group: "state_id",
  assignee: "assignee_ids",
  priority: "priority",
  label: "label_ids",
  cycle: "cycle_id",
  module: "module_ids",
  account: "account_id",
  milestone_stage: "milestone_stage_id",
  milestone_key: "milestone_stage_id",
  parent: "parent_id",
  due_date: "due_date",
  start_date: "start_date",
  updated_at: "updated_at",
  position: "position",
};
